// src/components/UnifiedDirectory.js

import React, { useEffect, useState, useCallback } from "react";
import DirectoryTable from "./DirectoryTable";
import { useTranslation } from "react-i18next";
import CheckboxList from "./CheckboxList";
import { notifyError } from "../utils/toasts";

function UnifiedDirectory({
  title,
  data = [],
  onAdd,
  onEdit,
  onDelete,
  fields = ["name"],
  displayFields,
  divisionId,
  // Опциональные пропсы для ассоциированных списков
  associatedList = [],
  fetchAssociatedList,
  fetchItemAssociations,
  updateItemAssociations,
  associatedIdsState,
  associatedIdsSetter,
  associatedLabel,
  itemIdKey = "id",

  // Новый проп, который мы передаем из DivisionsDirectory
  timezones = {},
}) {
  const { t } = useTranslation();
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);

  const [inputValues, setInputValues] = useState(() =>
    fields.reduce((acc, field) => {
      acc[field] = field === "status" ? "unavailable" : "";
      return acc;
    }, {})
  );

  const [region, setRegion] = useState("");
  const [zones, setZones] = useState([]);

  useEffect(() => {
    const allRegions = Object.keys(timezones);
    if (allRegions.length > 0) {
      if (!region) {
        setRegion(allRegions[0]);
        setZones(timezones[allRegions[0]]);
      }
    }
  }, [timezones, region]);

  const handleRegionChange = (e) => {
    const newRegion = e.target.value;
    setRegion(newRegion);
    setZones(timezones[newRegion] || []);
    // Сбрасываем выбранное значение timezone в форме
    setInputValues((prev) => ({
      ...prev,
      timezone: "",
    }));
  };

  const handleTimezoneChange = (e) => {
    const newTz = e.target.value;
    setInputValues((prev) => ({
      ...prev,
      timezone: newTz,
    }));
  };

  useEffect(() => {
    const columnsToDisplay = displayFields || fields;
    setColumns(
      columnsToDisplay.map((field) => ({ header: field, accessor: field }))
    );
    setFilteredData(data);
  }, [data, displayFields, fields]);

  // Фильтрация таблицы по поисковому запросу
  useEffect(() => {
    const lowercasedQuery = searchQuery.toLowerCase();
    const filtered = data.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(lowercasedQuery)
      )
    );
    setFilteredData(filtered);
  }, [searchQuery, data]);

  // При монтировании/смене divisionId - если нужно подгрузить связанные списки
  useEffect(() => {
    if (divisionId && fetchAssociatedList) {
      fetchAssociatedList();
    }
  }, [divisionId, fetchAssociatedList]);

  const handleAdd = async () => {
    // Собираем значения из полей (с учётом, что часть может быть 'status')
    // Допустим fields = ["name", "timezone"], тогда:
    const values = fields.map((field) =>
      field === "status"
        ? inputValues[field] === "available"
          ? "available"
          : "unavailable"
        : inputValues[field]
    );
    // Пример: fields[0] = "name", fields[1] = "timezone"
    // => values[0] = inputValues["name"], values[1] = inputValues["timezone"]
    await onAdd(...values); 
    resetForm();
  };

  const handleEdit = async () => {
    const values = fields.map((field) =>
      field === "status"
        ? inputValues[field] === "available"
          ? "available"
          : "unavailable"
        : inputValues[field]
    );
    await onEdit(selectedItem, ...values);

    // Если есть механика обновления ассоциаций
    if (selectedItem && updateItemAssociations && associatedIdsState) {
      await updateItemAssociations(selectedItem[itemIdKey], associatedIdsState);
    }
    resetForm();
  };

  const handleSelectItem = async (item) => {
    setSelectedItem(item);

    // Восстанавливаем значения полей в форму
    const newInputValues = fields.reduce((acc, field) => {
      if (field === "status") {
        acc[field] = item[field] === "available" ? "available" : "unavailable";
      } else {
        acc[field] = item[field] || "";
      }
      return acc;
    }, {});
    setInputValues(newInputValues);

    // Если у нас есть поле timezone, нужно понять, в каком регионе оно лежит
    if (fields.includes("timezone") && item.timezone && timezones) {
      // Ищем регион, в котором есть нужная timezone
      const tz = item.timezone;
      let foundRegion = "";
      let foundZones = [];
      for (const [reg, zoneList] of Object.entries(timezones)) {
        // zoneList = [{ timezone: "Africa/Accra", UTC: "+00:00"}, ...]
        if (zoneList.some((z) => z.timezone === tz)) {
          foundRegion = reg;
          foundZones = zoneList;
          break;
        }
      }
      // Устанавливаем region, обновляем список zones
      if (foundRegion) {
        setRegion(foundRegion);
        setZones(foundZones);
      } else {
        // Если таймзона не нашлась в списке, возможно, она устарела
        // или API не вернул такую
        // Можно сбросить region, zones = []
        setRegion("");
        setZones([]);
      }
    }

    // Если нужно загрузить ассоциации (чекбоксы)
    if (fetchItemAssociations && associatedIdsSetter) {
      try {
        const associations = await fetchItemAssociations(item[itemIdKey]);
        const ids = associations.map((assoc) => assoc.id);
        associatedIdsSetter(ids);
      } catch (error) {
        notifyError(error?.response?.data?.message || error.message);
      }
    }
  };

  const resetForm = () => {
    setSelectedItem(null);
    setInputValues(
      fields.reduce((acc, field) => {
        acc[field] = field === "status" ? "unavailable" : "";
        return acc;
      }, {})
    );
    if (associatedIdsSetter) {
      associatedIdsSetter([]);
    }
  };

  return (
    <div className="container">
      <h1>{title}</h1>
      <DirectoryTable
        columns={columns}
        data={filteredData}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onEdit={handleSelectItem}
        onDelete={async (item) => {
          await onDelete(item);
        }}
        onAdd={() => {
          resetForm();
        }}
      />

      <div className="card mt-4">
        <div className="card-body">
          <h3>{t("parameter_editor")}</h3>
          
          {fields
            .filter((field) => !["status", "timezone"].includes(field))
            .map((field, idx) => (
              <div key={idx} className="form-group">
                <label htmlFor={field}>
                  {t(field === "name" ? "name" : field)}
                </label>
                <input
                  type="text"
                  id={field}
                  className="form-control"
                  placeholder={t(`enter_${field}`)}
                  value={inputValues[field]}
                  onChange={(e) =>
                    setInputValues({
                      ...inputValues,
                      [field]: e.target.value,
                    })
                  }
                />
              </div>
            ))}
          
          {/* Если есть поле status, показываем чекбокс доступности */}
          {fields.includes("status") && (
            <div className="form-check mt-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="status"
                checked={inputValues["status"] === "available"}
                onChange={(e) =>
                  setInputValues({
                    ...inputValues,
                    status: e.target.checked ? "available" : "unavailable",
                  })
                }
              />
              <label className="form-check-label" htmlFor="status">
                {t("status_available")}
              </label>
            </div>
          )}

          {/* Если нужно поле timezone, показываем два селекта */}
          {fields.includes("timezone") && Object.keys(timezones).length > 0 && (
            <div className="form-group">
              <label>{t("region")}</label>
              <select
                className="form-control"
                value={region}
                onChange={handleRegionChange}
              >
                {Object.keys(timezones).map((reg) => (
                  <option key={reg} value={reg}>
                    {reg}
                  </option>
                ))}
              </select>

              <label className="mt-2">{t("time")}</label>
              <select
                className="form-control"
                value={inputValues["timezone"]}
                onChange={handleTimezoneChange}
              >
                <option value="">
                  {t("select_timezone")}
                </option>
                {zones.map((z) => (
                  <option key={z.timezone} value={z.timezone}>
                    {`UTC ${z.UTC} ${z.timezone}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Условно отображаем список с чекбоксами (если используется) */}
          {associatedList.length > 0 && (
            <CheckboxList
              items={associatedList}
              selectedIds={associatedIdsState}
              setSelectedIds={associatedIdsSetter}
              label={associatedLabel}
            />
          )}

          <button
            className="btn btn-primary mt-2"
            onClick={selectedItem ? handleEdit : handleAdd}
          >
            {selectedItem ? t("save") : t("create")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UnifiedDirectory;
